/**
 * PropertyDetailView Component
 *
 * Two-column layout showing property details (left) and map (right).
 * Designed to be reusable across different contexts (detail page, modals, etc.)
 *
 * @param {Object} property - Property data object
 * @param {Array} transactions - Transaction history array (optional)
 * @param {boolean} showMap - Whether to show the map (default: true)
 * @param {Function} onClose - Optional close handler
 * @param {Object} importNames - Optional: the names of imports, by id, where the page may read them (administrators)
 * @param {boolean} canOpenImports - Optional: whether this reader may open an import's own page (administrators)
 *
 * ⚠️ WHERE A HOME CAME FROM is the one source rule's to say (utils/propertyOrigin.js). Until Oct 2026 this page
 * called every record's creation "Scraped", and an import's home showed "Scraped 09 Oct 2026" with nothing of
 * what its file had said (Lee: "the detail is very sparse … and they show a 'scrape' of 9th october").
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { db, functions } from "../../../firebase";
import { MailshotRepository } from "../../../models/MailshotRepository";
import {
  FaHome,
  FaBed,
  FaBath,
  FaPoundSign,
  FaMapMarkerAlt,
  FaUser,
  FaPhone,
  FaCalendar,
  FaKey,
  FaBuilding,
  FaExternalLinkAlt,
  FaClock,
  FaTag,
  FaImage,
  FaFileImage,
  FaVideo,
  FaRuler,
  FaStar,
  FaTrophy,
  FaGavel,
  FaBolt,
  FaCheckCircle,
  FaInfoCircle,
  FaListUl,
  FaFileImport,
} from "react-icons/fa";
import BaseMap from "../../map/BaseMap";
import DeliveryStatusBadge from "../DeliveryStatusBadge";
import GeocodeButton from "../GeocodeButton";
import Badge from "../Badge";
import { MatchConfidenceBadge } from "../MatchConfidenceBadge";
import { describeMatch } from "../../../models/mailability";
import { originOf, dayOf, fileFieldsOf, linkedImportsOf, importHref } from "../../../utils/propertyOrigin";
import "./PropertyDetailView.scss";

export const PropertyDetailView = ({
  property,
  transactions = [],
  showMap = true,
  onClose,
  importNames = {},
  canOpenImports = false,
}) => {
  // where this record came from, and what an import's file said of it (null unless an import MADE it)
  const origin = originOf(property);
  const isImports = origin.kind === "import";
  const fromFile = fileFieldsOf(property);
  const linkedImports = linkedImportsOf(property);
  const importName = (id) => (id && importNames && typeof importNames[id] === "string" && importNames[id].trim()) || null;
  const [mailshots, setMailshots] = React.useState([]);
  const [loadingMailshots, setLoadingMailshots] = React.useState(false);

  const mailshotRepository = useMemo(
    () => new MailshotRepository(db, functions),
    []
  );

  // Fetch mailshot data when property changes
  React.useEffect(() => {
    const fetchMailshots = async () => {
      if (!property?.mailshotIds || property.mailshotIds.length === 0) {
        setMailshots([]);
        return;
      }

      try {
        setLoadingMailshots(true);
        const mailshotPromises = property.mailshotIds.map(async (mailshotId) => {
          try {
            return await mailshotRepository.findById(mailshotId);
          } catch (err) {
            console.error("Error fetching mailshot:", mailshotId, err);
            return null;
          }
        });

        const fetchedMailshots = (await Promise.all(mailshotPromises)).filter(Boolean);
        setMailshots(fetchedMailshots);
      } catch (err) {
        console.error("Error fetching mailshots:", err);
      } finally {
        setLoadingMailshots(false);
      }
    };

    fetchMailshots();
  }, [property?.id, property?.mailshotIds, mailshotRepository]);

  const [coordinates, setCoordinates] = React.useState({
    lat: property.latitude,
    lng: property.longitude,
  });

  // Refs to track marker and map instances
  const markerRef = React.useRef(null);
  const mapRef = React.useRef(null);
  const [mapLoaded, setMapLoaded] = React.useState(false);
  const hasInitialized = React.useRef(false); // Track if we've already initialized once

  // Update coordinates when property changes (e.g., after geocoding)
  React.useEffect(() => {
    if (property.latitude && property.longitude) {
      setCoordinates({
        lat: property.latitude,
        lng: property.longitude,
      });
    }
  }, [property.latitude, property.longitude]);

  // Create native marker when map loads and coordinates change
  React.useEffect(() => {
    if (
      mapLoaded &&
      mapRef.current &&
      coordinates.lat &&
      coordinates.lng &&
      window.google?.maps
    ) {
      // Remove old marker if exists
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }

      // Create new marker using native Google Maps API with default pin
      try {
        markerRef.current = new window.google.maps.Marker({
          position: coordinates,
          map: mapRef.current,
          title: property.address,
          optimized: false,
        });
      } catch (error) {
        console.error("Error creating marker:", error);
      }
    }

    // Cleanup function
    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
    };
  }, [mapLoaded, coordinates, property.address]);

  // Callback for when map loads
  const handleMapLoad = React.useCallback((map) => {
    // Check if marker exists and update its map reference on subsequent loads
    if (markerRef.current && hasInitialized.current) {
      markerRef.current.setMap(map);
    }

    mapRef.current = map;

    // Only set mapLoaded on the first load to avoid destroying marker
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      setMapLoaded(true);
    }
  }, []); // Empty deps - stable callback

  // Map configuration - must be before early return
  const hasLocation = coordinates.lat && coordinates.lng;

  // Memoize mapCenter to prevent BaseMap from re-rendering unnecessarily
  const mapCenter = React.useMemo(() => {
    return hasLocation ? coordinates : { lat: 52.27, lng: -1.87 };
  }, [coordinates.lat, coordinates.lng, hasLocation]);

  if (!property) {
    return (
      <div className="property-detail-view property-detail-view--empty">
        <div className="empty-state">
          <FaHome />
          <p>Property not found</p>
        </div>
      </div>
    );
  }

  // Format helpers
  const formatPrice = (price) => {
    if (!price) return "N/A";
    if (typeof price === "string" && price.includes("£")) {
      return price;
    }
    return `£${Number(price).toLocaleString()}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleGeocodeSuccess = (data) => {
    setCoordinates({
      lat: data.latitude,
      lng: data.longitude,
    });
  };

  // Helper functions for new fields
  const calculatePricePerSqFt = () => {
    if (!property.price || !property.displaySize) return null;
    const price = typeof property.price === 'string' 
      ? parseFloat(property.price.replace(/[£,]/g, ''))
      : property.price;
    const size = parseFloat(property.displaySize.replace(/[^\d.]/g, ''));
    if (isNaN(price) || isNaN(size) || size === 0) return null;
    return Math.round(price / size);
  };

  const getDaysOnMarket = () => {
    if (!property.firstVisibleDate) return null;
    const firstVisible = property.firstVisibleDate.toDate 
      ? property.firstVisibleDate.toDate() 
      : new Date(property.firstVisibleDate);
    const now = new Date();
    const diffTime = Math.abs(now - firstVisible);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getTimeSinceUpdate = () => {
    if (!property.updateDate) return null;
    const updateDate = property.updateDate.toDate 
      ? property.updateDate.toDate() 
      : new Date(property.updateDate);
    const now = new Date();
    const diffTime = Math.abs(now - updateDate);
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  const pricePerSqFt = calculatePricePerSqFt();
  const daysOnMarket = getDaysOnMarket();
  const timeSinceUpdate = getTimeSinceUpdate();

  // Get media from new or legacy structure
  const images = property.mediaContent?.images || property.images || [];
  const floorplans = property.mediaContent?.floorplans || [];
  const virtualTours = property.mediaContent?.virtualTours || [];
  const epcRatings = property.mediaContent?.epcRatings;

  // Fallback to mediaCounts if arrays are empty (bug in older scrapes)
  const imageCount = images.length || property.mediaCounts?.numberOfImages || 0;
  const floorplanCount = floorplans.length || property.mediaCounts?.numberOfFloorplans || 0;
  const virtualTourCount = virtualTours.length || property.mediaCounts?.numberOfVirtualTours || 0;

  // Extract branchLocation from branch field if not present (backwards compatibility)
  const branchLocation = property.agent?.branchLocation || 
    (property.agent?.branch && property.agent.branch.includes(',') 
      ? property.agent.branch.split(',')[1].trim() 
      : null);

  // Check if we have any quick stats to show
  const hasQuickStats =
    property.bedrooms ||
    property.bathrooms ||
    property.propertyType ||
    property.tenure;

  return (
    <div className="property-detail-view">
      <div className="property-detail-view__container">
        {/* Left Panel - Property Details */}
        <div className="property-detail-view__details">
          {/* Quick Stats */}
          {hasQuickStats && (
            <div className="detail-stats">
              {property.bedrooms && (
                <div className="detail-stat">
                  <FaBed className="detail-stat__icon" />
                  <span>
                    {property.bedrooms} bed{property.bedrooms !== 1 ? "s" : ""}
                  </span>
                </div>
              )}
              {property.bathrooms && (
                <div className="detail-stat">
                  <FaBath className="detail-stat__icon" />
                  <span>
                    {property.bathrooms} bath
                    {property.bathrooms !== 1 ? "s" : ""}
                  </span>
                </div>
              )}
              {property.propertyType && (
                <div className="detail-stat">
                  <FaBuilding className="detail-stat__icon" />
                  <span>{property.propertyType}</span>
                </div>
              )}
              {property.tenure && (
                <div className="detail-stat">
                  <FaKey className="detail-stat__icon" />
                  <span>{property.tenure}</span>
                </div>
              )}
              {property.displaySize && (
                <div className="detail-stat">
                  <FaRuler className="detail-stat__icon" />
                  <span>{property.displaySize}</span>
                </div>
              )}
            </div>
          )}

          {/* Marketing Badges */}
          {(property.premiumListing || property.featuredProperty || property.addedOrReduced || 
            property.auction || property.development || (daysOnMarket !== null && daysOnMarket <= 7)) && (
            <div className="detail-section detail-section--compact">
              <div className="property-badges">
                {property.premiumListing && (
                  <Badge variant="purple" icon={<FaStar />}>
                    Premium Listing
                  </Badge>
                )}
                {property.featuredProperty && (
                  <Badge variant="warning" icon={<FaTrophy />}>
                    Featured
                  </Badge>
                )}
                {property.addedOrReduced && property.addedOrReduced.toLowerCase().includes('reduced') && (
                  <Badge variant="danger" icon={<FaBolt />}>
                    Price Reduced
                  </Badge>
                )}
                {property.addedOrReduced && property.addedOrReduced.toLowerCase().includes('added') && daysOnMarket <= 7 && (
                  <Badge variant="success" icon={<FaCheckCircle />}>
                    New Listing
                  </Badge>
                )}
                {property.auction && (
                  <Badge variant="info" icon={<FaGavel />}>
                    Auction
                  </Badge>
                )}
                {property.development && (
                  <Badge variant="primary" icon={<FaBuilding />}>
                    New Development
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* Status Badge */}
          {property.deliveryStatus && (
            <div className="detail-section detail-section--compact">
              <h3 className="detail-section__title">
                <FaTag className="section-icon" />
                Delivery Status
              </h3>
              <DeliveryStatusBadge
                status={property.deliveryStatus}
                size="large"
                showLabel
              />
            </div>
          )}

          {/* Mailshots */}
          {property.mailshotIds && property.mailshotIds.length > 0 && (
            <div className="detail-section detail-section--compact">
              <h3 className="detail-section__title">
                <FaTag className="section-icon" />
                Included in Mailshots
              </h3>
              {loadingMailshots ? (
                <p style={{ fontSize: "0.875rem", color: "#6b7280" }}>Loading mailshots...</p>
              ) : (
                <div className="detail-grid">
                  {property.mailshotIds.map((mailshotId, index) => {
                    const mailshot = mailshots.find(m => m.id === mailshotId);
                    return (
                      <div key={mailshotId} className="detail-item" style={{ gridColumn: "1 / -1" }}>
                        <span className="detail-item__label">Mailshot {index + 1}</span>
                        <span className="detail-item__value">
                          <Link 
                            to={`/dashboard/properties/mailshot/${mailshotId}`}
                            style={{ color: "#6366f1", textDecoration: "none", display: "block" }}
                          >
                            {mailshot?.name || "View Mailshot"} <FaExternalLinkAlt style={{ fontSize: "0.75rem", marginLeft: "0.25rem" }} />
                          </Link>
                          {mailshot?.mailedOutAt && (
                            <div style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.25rem" }}>
                              Mailed: {new Date(mailshot.mailedOutAt.seconds * 1000).toLocaleDateString("en-GB", {
                                day: "numeric",
                                month: "short",
                                year: "numeric"
                              })}
                            </div>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Match Confidence — ⚠️ explained by describeMatch(), which knows
              which address engine graded the record. The text here used to
              describe every grade in v1's terms, so since the switch to the v2
              engine a v2 property was given the wrong explanation. */}
          {(() => {
            const m = describeMatch(property);
            if (!property.matchConfidence && !property.shadowRun) return null;
            return (
              <div className="detail-section detail-section--compact">
                <h3 className="detail-section__title">
                  <FaCheckCircle className="section-icon" />
                  Address Match Confidence
                </h3>
                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                  <MatchConfidenceBadge confidence={property.matchConfidence} size="large" />
                  <strong style={{ fontSize: "0.9375rem" }}>{m.headline}</strong>
                  <Badge
                    variant={m.mailable ? "success" : "warning"}
                    size="small"
                    title={m.mailable
                      ? "Meets the mailing rule with the default switches"
                      : "Held back by the mailing rule with the default switches"}
                  >
                    {m.mailable ? "Mailable" : "Not mailed by default"}
                  </Badge>
                </div>
                <ul style={{ fontSize: "0.875rem", color: "#4b5563", margin: "0.5rem 0 0", paddingLeft: "1.1rem" }}>
                  {m.lines.map((line) => <li key={line}>{line}</li>)}
                </ul>
              </div>
            );
          })()}

          {/* An import's home has no match grade, so the section above is absent for it. This says where its
              address came from instead, in its own words: "saved before addresses were graded", which is what
              the mailing rule's own words for a record with no grade say, is not true of an import. */}
          {isImports && !property.matchConfidence && !property.shadowRun && (() => {
            const m = describeMatch(property);
            const day = dayOf(origin.when);
            const name = importName(origin.id);
            return (
              <div className="detail-section detail-section--compact" data-testid="address-from-import">
                <h3 className="detail-section__title">
                  <FaFileImport className="section-icon" />
                  Where this address came from
                </h3>
                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                  <strong style={{ fontSize: "0.9375rem" }}>From a supplier's file, not from a scraper</strong>
                  <Badge variant={m.mailable ? "success" : "warning"} size="small">
                    {m.mailable ? "Mailable" : "Not mailed by default"}
                  </Badge>
                </div>
                <ul style={{ fontSize: "0.875rem", color: "#4b5563", margin: "0.5rem 0 0", paddingLeft: "1.1rem" }}>
                  <li>Brought in by an import{name ? ` (${name})` : ""}{day ? ` on ${day}` : ""}.</li>
                  <li>Its address is the file's own. Each row was printed by the mailshot's own address function as it was imported, and a row whose mailshot would have lost part of its address was refused.</li>
                  <li>No scraper matched it, so it has no match grade. The mailing rule mails it, as it mails any record with no grade.</li>
                  <li>The file gives no price, bedrooms, type, photos or map position: those parts of this page are empty for that reason.</li>
                </ul>
              </div>
            );
          })()}

          {/* What the import's file said of this home. Until Oct 2026 these were in Raw data only (administrators). */}
          {fromFile && (
            <div className="detail-section" data-testid="from-the-file">
              <h3 className="detail-section__title">
                <FaFileImport className="section-icon" />
                From the import's file
              </h3>
              {!isImports && (
                <p style={{ fontSize: "0.875rem", color: "#4b5563", margin: "0 0 0.5rem" }}>
                  This home was first brought in by an import{importName(property.importId) ? ` (${importName(property.importId)})` : ""}. A scrape has saved it since, so it counts as the scrape's; this is what the file said of it.
                </p>
              )}
              <div className="detail-grid">
                {fromFile.rows.map((row) => (
                  <div className="detail-item" key={row.label}>
                    <span className="detail-item__label">{row.label}</span>
                    <span className="detail-item__value">{row.value}</span>
                  </div>
                ))}
              </div>
              {/* Only while the home is still the import's own: a scrape that saves it writes its own address,
                  and the parts would then be neither the file's nor certainly the scrape's. */}
              {isImports && fromFile.parts.length > 0 && (
                <>
                  <p style={{ fontSize: "0.8125rem", color: "#6b7280", margin: "0.75rem 0 0.25rem" }}>
                    The address in parts. The mailshot is printed from these, not from the one-line address above:
                  </p>
                  <div className="detail-grid">
                    {fromFile.parts.map((part) => (
                      <div className="detail-item" key={part.label}>
                        <span className="detail-item__label">{part.label}</span>
                        <span className="detail-item__value">{part.value}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Basic Information */}
          <div className="detail-section">
            <h3 className="detail-section__title">
              <FaHome className="section-icon" />
              Property Details
            </h3>
            <div className="detail-grid">
              {property.propertyStatus && (
                <div className="detail-item">
                  <span className="detail-item__label">Status</span>
                  <span className="detail-item__value">
                    {property.propertyStatus}
                  </span>
                </div>
              )}
              {property.propertyType && (
                <div className="detail-item">
                  <span className="detail-item__label">Type</span>
                  <span className="detail-item__value">
                    {property.propertyTypeFullDescription || property.propertyType}
                  </span>
                </div>
              )}
              {property.displaySize && (
                <div className="detail-item">
                  <span className="detail-item__label">Size</span>
                  <span className="detail-item__value">
                    {property.displaySize}
                  </span>
                </div>
              )}
              {property.channel && (
                <div className="detail-item">
                  <span className="detail-item__label">Channel</span>
                  <span className="detail-item__value">
                    {property.channel}
                  </span>
                </div>
              )}
              {property.transactionType && (
                <div className="detail-item">
                  <span className="detail-item__label">Transaction Type</span>
                  <span className="detail-item__value">
                    {property.transactionType}
                  </span>
                </div>
              )}
              {property.bedrooms && (
                <div className="detail-item">
                  <span className="detail-item__label">Bedrooms</span>
                  <span className="detail-item__value">
                    {property.bedrooms}
                  </span>
                </div>
              )}
              {property.bathrooms && (
                <div className="detail-item">
                  <span className="detail-item__label">Bathrooms</span>
                  <span className="detail-item__value">
                    {property.bathrooms}
                  </span>
                </div>
              )}
              {property.tenure && (
                <div className="detail-item">
                  <span className="detail-item__label">Tenure</span>
                  <span className="detail-item__value">{property.tenure}</span>
                </div>
              )}
              {property.propertyId && (
                <div className="detail-item">
                  <span className="detail-item__label">Property ID</span>
                  <span className="detail-item__value">
                    {property.propertyId}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Summary */}
          {property.summary && (
            <div className="detail-section">
              <h3 className="detail-section__title">
                <FaInfoCircle className="section-icon" />
                Summary
              </h3>
              <p className="detail-description">{property.summary}</p>
            </div>
          )}

          {/* Key Features */}
          {property.keyFeatures && property.keyFeatures.length > 0 && (
            <div className="detail-section">
              <h3 className="detail-section__title">
                <FaListUl className="section-icon" />
                Key Features
              </h3>
              <ul className="key-features-list">
                {property.keyFeatures.map((feature, index) => (
                  <li key={index} className="key-feature-item">
                    <FaCheckCircle className="key-feature-icon" />
                    {typeof feature === 'string' ? feature : feature.description}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Media Content */}
          {(imageCount > 0 || floorplanCount > 0 || virtualTourCount > 0 || epcRatings) && (
            <div className="detail-section">
              <h3 className="detail-section__title">
                <FaImage className="section-icon" />
                Media & Documents
              </h3>
              <div className="media-stats">
                {imageCount > 0 && (
                  <div className="media-stat">
                    <FaImage className="media-stat__icon" />
                    <span className="media-stat__count">{imageCount}</span>
                    <span className="media-stat__label">Photo{imageCount !== 1 ? 's' : ''}</span>
                  </div>
                )}
                {floorplanCount > 0 && (
                  <div className="media-stat">
                    <FaFileImage className="media-stat__icon" />
                    <span className="media-stat__count">{floorplanCount}</span>
                    <span className="media-stat__label">Floorplan{floorplanCount !== 1 ? 's' : ''}</span>
                  </div>
                )}
                {virtualTourCount > 0 && (
                  <div className="media-stat">
                    <FaVideo className="media-stat__icon" />
                    <span className="media-stat__count">{virtualTourCount}</span>
                    <span className="media-stat__label">Virtual Tour{virtualTourCount !== 1 ? 's' : ''}</span>
                  </div>
                )}
              </div>
              {virtualTours.length > 0 && (
                <div className="virtual-tours">
                  {virtualTours.map((tour, index) => (
                    <a
                      key={index}
                      href={tour.url || tour}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="virtual-tour-button"
                    >
                      <FaVideo /> View Virtual Tour {virtualTours.length > 1 ? `(${index + 1})` : ''}
                    </a>
                  ))}
                </div>
              )}
              {epcRatings && (
                <div className="epc-ratings">
                  {epcRatings.currentRating && (
                    <Badge variant="info">
                      Current EPC: {epcRatings.currentRating}
                    </Badge>
                  )}
                  {epcRatings.potentialRating && (
                    <Badge variant="success">
                      Potential EPC: {epcRatings.potentialRating}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Pricing Information. An import's file gives no price: the section is left out for an import's home
              with none, where it used to say "Current Price: N/A". Every other record is as it was. */}
          {!(isImports && !property.price && !property.priceQualifier && !property.lastSalePrice && !property.lastSaleDate) && (
          <div className="detail-section">
            <h3 className="detail-section__title">
              <FaPoundSign className="section-icon" />
              Pricing
            </h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-item__label">Current Price</span>
                <span className="detail-item__value detail-item__value--highlight">
                  {formatPrice(property.price)}
                </span>
              </div>
              {pricePerSqFt && (
                <div className="detail-item">
                  <span className="detail-item__label">Price per sq ft</span>
                  <span className="detail-item__value">
                    £{pricePerSqFt.toLocaleString()}/sq ft
                  </span>
                </div>
              )}
              {property.priceQualifier && (
                <div className="detail-item">
                  <span className="detail-item__label">Qualifier</span>
                  <span className="detail-item__value">
                    {property.priceQualifier}
                  </span>
                </div>
              )}
              {property.priceType && (
                <div className="detail-item">
                  <span className="detail-item__label">Price Type</span>
                  <span className="detail-item__value">
                    {property.priceType}
                  </span>
                </div>
              )}
              {property.lastSalePrice && (
                <div className="detail-item">
                  <span className="detail-item__label">Last Sale Price</span>
                  <span className="detail-item__value">
                    {formatPrice(property.lastSalePrice)}
                  </span>
                </div>
              )}
              {property.lastSaleDate && (
                <div className="detail-item">
                  <span className="detail-item__label">Last Sale Date</span>
                  <span className="detail-item__value">
                    {formatDate(property.lastSaleDate)}
                  </span>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Agent Information */}
          {(property.agent || property.agentName || property.agentPhone) && (
            <div className="detail-section">
              <h3 className="detail-section__title">
                <FaUser className="section-icon" />
                Agent
              </h3>
              {property.agent?.logoUrl && (
                <div className="agent-logo">
                  <img src={property.agent.logoUrl} alt={property.agent.name || 'Agent logo'} />
                </div>
              )}
              <div className="agent-badges">
                {property.agent?.hasBrandPlus && (
                  <Badge variant="purple" icon={<FaStar />}>
                    Rightmove Premium Agent
                  </Badge>
                )}
                {property.agent?.isDeveloper && (
                  <Badge variant="info" icon={<FaBuilding />}>
                    Property Developer
                  </Badge>
                )}
                {property.agent?.isCommercial && (
                  <Badge variant="success" icon={<FaBuilding />}>
                    Commercial Specialist
                  </Badge>
                )}
              </div>
              <div className="detail-grid">
                {property.agent?.name && (
                  <div className="detail-item">
                    <span className="detail-item__label">Name</span>
                    <span className="detail-item__value">
                      {property.agent.name}
                    </span>
                  </div>
                )}
                {property.agent?.company && (
                  <div className="detail-item">
                    <span className="detail-item__label">Company</span>
                    <span className="detail-item__value">
                      {property.agent.company}
                    </span>
                  </div>
                )}
                {property.agent?.branch && (
                  <div className="detail-item">
                    <span className="detail-item__label">Branch</span>
                    <span className="detail-item__value">
                      {property.agent.branch}
                    </span>
                  </div>
                )}
                {property.agent?.branchId && (
                  <div className="detail-item">
                    <span className="detail-item__label">Branch ID</span>
                    <span className="detail-item__value">
                      {property.agent.branchId}
                    </span>
                  </div>
                )}
                {branchLocation && (
                  <div className="detail-item">
                    <span className="detail-item__label">Branch Location</span>
                    <span className="detail-item__value">
                      {branchLocation}
                    </span>
                  </div>
                )}
                {property.agent?.phone && (
                  <div className="detail-item">
                    <span className="detail-item__label">Phone</span>
                    <span className="detail-item__value">
                      <FaPhone className="inline-icon" />
                      {property.agent.phone}
                    </span>
                  </div>
                )}
                {property.agent?.branchUrl && (
                  <div className="detail-item detail-item--full">
                    <span className="detail-item__label">Rightmove Page</span>
                    <span className="detail-item__value">
                      <a
                        href={`https://www.rightmove.co.uk${property.agent.branchUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="detail-link"
                      >
                        View Agent on Rightmove
                        <FaExternalLinkAlt className="detail-link__icon" />
                      </a>
                    </span>
                  </div>
                )}
                {property.agent?.nameSlug && (
                  <div className="detail-item">
                    <span className="detail-item__label">Name Slug</span>
                    <span className="detail-item__value">
                      {property.agent.nameSlug}
                    </span>
                  </div>
                )}
                {property.agent?.branchSlug && (
                  <div className="detail-item">
                    <span className="detail-item__label">Branch Slug</span>
                    <span className="detail-item__value">
                      {property.agent.branchSlug}
                    </span>
                  </div>
                )}
                {property.agentName && !property.agent?.name && (
                  <div className="detail-item">
                    <span className="detail-item__label">Name</span>
                    <span className="detail-item__value">
                      {property.agentName}
                    </span>
                  </div>
                )}
                {property.agentPhone && !property.agent?.phone && (
                  <div className="detail-item">
                    <span className="detail-item__label">Phone</span>
                    <span className="detail-item__value">
                      <FaPhone className="inline-icon" />
                      {property.agentPhone}
                    </span>
                  </div>
                )}
                {property.agentAddress && (
                  <div className="detail-item detail-item--full">
                    <span className="detail-item__label">Address</span>
                    <span className="detail-item__value">
                      {property.agentAddress}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Location */}
          <div className="detail-section">
            <h3 className="detail-section__title">
              <FaMapMarkerAlt className="section-icon" />
              Location
            </h3>
            <div className="detail-grid">
              {property.address && (
                <div className="detail-item detail-item--full">
                  <span className="detail-item__label">Address</span>
                  <span className="detail-item__value">{property.address}</span>
                </div>
              )}
              {property.town && (
                <div className="detail-item">
                  <span className="detail-item__label">Town</span>
                  <span className="detail-item__value">{property.town}</span>
                </div>
              )}
              {property.postcode && (
                <div className="detail-item">
                  <span className="detail-item__label">Postcode</span>
                  <span className="detail-item__value">
                    {property.postcode}
                  </span>
                </div>
              )}
              {hasLocation && (
                <>
                  <div className="detail-item">
                    <span className="detail-item__label">Latitude</span>
                    <span className="detail-item__value">
                      {coordinates.lat.toFixed(6)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-item__label">Longitude</span>
                    <span className="detail-item__value">
                      {coordinates.lng.toFixed(6)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="detail-section">
            <h3 className="detail-section__title">
              <FaClock className="section-icon" />
              Listing Timeline
            </h3>
            <div className="detail-grid">
              {property.firstVisibleDate && (
                <div className="detail-item">
                  <span className="detail-item__label">First Listed (Rightmove)</span>
                  <span className="detail-item__value">
                    {formatDate(property.firstVisibleDate)}
                  </span>
                </div>
              )}
              {daysOnMarket && (
                <div className="detail-item">
                  <span className="detail-item__label">Days on Market</span>
                  <span className="detail-item__value">
                    {daysOnMarket} day{daysOnMarket !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
              {property.updateDate && (
                <div className="detail-item">
                  <span className="detail-item__label">Last Updated</span>
                  <span className="detail-item__value">
                    {formatDate(property.updateDate)}
                    {timeSinceUpdate && ` (${timeSinceUpdate})`}
                  </span>
                </div>
              )}
              {property.dateAdded && (
                <div className="detail-item">
                  <span className="detail-item__label">Date Added (Our System)</span>
                  <span className="detail-item__value">
                    {formatDate(property.dateAdded)}
                  </span>
                </div>
              )}
              {property.listingUpdate && (
                <div className="detail-item">
                  <span className="detail-item__label">Listing Update</span>
                  <span className="detail-item__value">
                    {formatDate(property.listingUpdate)}
                  </span>
                </div>
              )}
              {/* "Scraped" only for a scrape's home. It said "Scraped" for every record: an import's, a HubSpot
                  quote's and a hand-added address's too. */}
              {origin.when && (
                <div className="detail-item" data-testid="origin-when">
                  <span className="detail-item__label">{origin.did}</span>
                  <span className="detail-item__value">
                    {dayOf(origin.when)}
                  </span>
                </div>
              )}
              {isImports && (
                <div className="detail-item" data-testid="origin-import">
                  <span className="detail-item__label">Import</span>
                  <span className="detail-item__value">
                    {canOpenImports && origin.id ? (
                      <Link to={importHref(origin.id)} className="detail-link">
                        {importName(origin.id) || "An import of a supplier's file"}
                        <FaExternalLinkAlt className="detail-link__icon" />
                      </Link>
                    ) : (
                      importName(origin.id) || "An import of a supplier's file"
                    )}
                  </span>
                </div>
              )}
              {linkedImports.length > 0 && (
                <div className="detail-item" data-testid="linked-imports">
                  <span className="detail-item__label">Also in {linkedImports.length === 1 ? "an import" : `${linkedImports.length} imports`}</span>
                  <span className="detail-item__value">
                    {canOpenImports
                      ? linkedImports.map((id, index) => (
                          <React.Fragment key={id}>
                            {index > 0 && ", "}
                            <Link to={importHref(id)} className="detail-link">{importName(id) || "an import"}</Link>
                          </React.Fragment>
                        ))
                      : (linkedImports.map((id) => importName(id)).filter(Boolean).join(", ") ||
                        `${linkedImports.length === 1 ? "An import's file" : "Imports' files"} listed this home too: we held it already, so it was linked, not copied`)}
                  </span>
                </div>
              )}
              {property.jobId && (
                <div className="detail-item">
                  <span className="detail-item__label">Job ID</span>
                  <span className="detail-item__value">
                    <Link
                      to={`/dashboard/properties/scrapes/${property.jobId}`}
                      className="detail-link"
                    >
                      {property.jobId}
                      <FaExternalLinkAlt className="detail-link__icon" />
                    </Link>
                  </span>
                </div>
              )}
              {property.url && (
                <div className="detail-item detail-item--full">
                  <span className="detail-item__label">Listing URL</span>
                  <span className="detail-item__value">
                    <a
                      href={property.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="detail-link"
                    >
                      View on Rightmove
                      <FaExternalLinkAlt className="detail-link__icon" />
                    </a>
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {property.description && (
            <div className="detail-section">
              <h3 className="detail-section__title">Description</h3>
              <p className="detail-description">{property.description}</p>
            </div>
          )}

          {/* Transaction History */}
          {transactions && transactions.length > 0 && (
            <div className="detail-section">
              <h3 className="detail-section__title">
                <FaCalendar className="section-icon" />
                Transaction History ({transactions.length})
              </h3>
              <div className="transaction-list">
                {transactions.map((transaction, index) => (
                  <div key={index} className="transaction-item">
                    <div className="transaction-item__date">
                      {formatDate(transaction.date)}
                    </div>
                    <div className="transaction-item__price">
                      {formatPrice(transaction.price)}
                    </div>
                    {transaction.type && (
                      <div className="transaction-item__type">
                        {transaction.type}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Map */}
        {showMap && (
          <div className="property-detail-view__map">
            {hasLocation ? (
              <BaseMap
                center={mapCenter}
                zoom={15}
                height="100%"
                width="100%"
                onMapLoad={handleMapLoad}
              />
            ) : (
              <GeocodeButton
                propertyId={property.id}
                address={property.address}
                postcode={property.postcode}
                onSuccess={handleGeocodeSuccess}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

PropertyDetailView.propTypes = {
  property: PropTypes.object.isRequired,
  transactions: PropTypes.array,
  showMap: PropTypes.bool,
  onClose: PropTypes.func,
  importNames: PropTypes.object,
  canOpenImports: PropTypes.bool,
};

export default PropertyDetailView;
